import { ChangeDetectorRef, Component, ComponentRef, Inject, OnDestroy, OnInit, AfterViewInit, ViewChild, ViewContainerRef } from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, InteractionType, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ConfigService } from './services/config.service';
import { HeaderComponent } from './shared/header/header.component';
import { FooterComponent } from './shared/footer/footer.component';
import { environment } from '../environments/environment';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent  implements OnInit, AfterViewInit, OnDestroy {
  title = 'Field Supervisor Portal';
  isIframe: boolean;
  componentReady = false;
  private readonly _destroying$ = new Subject<void>();
  loginDisplay: boolean;

  @ViewChild('headerContainer', { read: ViewContainerRef }) headerContainer!: ViewContainerRef;
  @ViewChild('footerContainer', { read: ViewContainerRef }) footerContainer!: ViewContainerRef;

  private headerRef?: ComponentRef<HeaderComponent>;
  private footerRef?: ComponentRef<FooterComponent>;

  constructor(
      @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
      private authService: MsalService,
      private msalBroadcastService: MsalBroadcastService,
      private configService: ConfigService,
      private cdr: ChangeDetectorRef
    ) {
    }

  ngOnInit(): void {
    // Temporary verification: Shows which environment file is loaded (non-prod only)
    if (!environment.production) {
      console.log('🔍 ENVIRONMENT VERIFICATION:', {
        envName: (environment as any).envName,
        production: environment.production,
        timestamp: environment.Build_Timestamp
      });
    }
    
    this.setThemeBasedOnDomain();
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.handleRedirects();
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      })
  }

  async handleRedirects()
  {
    try {
      await this.authService.instance.handleRedirectPromise();
    } catch (error) {
      console.error('Error in handleRedirectPromise:', error);
    }
  }

  ngAfterViewInit(): void {
    this.componentReady = true;
    this.createComponents();
    this.cdr.detectChanges();
  }


  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount(){
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  login() {
    debugger
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest){
        this.authService.loginPopup({...this.msalGuardConfig.authRequest} as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
        } else {
          this.authService.loginPopup()
            .subscribe((response: AuthenticationResult) => {
              this.authService.instance.setActiveAccount(response.account);
            });
      }
    } else {
      if (this.msalGuardConfig.authRequest){
        this.authService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
      } else {
        this.authService.loginRedirect();
      }
    }
  }

   setThemeBasedOnDomain() {
    var domain = window.location.hostname;
    var theme = 'tot-theme'; // default theme
    var config = this.configService;

    if (config.totDomain && domain.includes(config.totDomain)) {
        theme = 'tot-theme';
    } else if (config.teachDomain && domain.includes(config.teachDomain)) {
        theme = 'teach-theme';
    }
    document.body.className = theme;
  }

  logout() {
    this.authService.logout();
  }

  private createComponents(): void {
    if (this.headerContainer) {
      this.headerRef = this.headerContainer.createComponent(HeaderComponent);
    }
    
    if (this.footerContainer) {
      this.footerRef = this.footerContainer.createComponent(FooterComponent);
    }
  }

  ngOnDestroy(): void {
    this.headerRef?.destroy();
    this.footerRef?.destroy();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
